/**
 * External Data Context
 * 
 * Shared state management for External Data Sources Dashboard
 * Provides geographic filters, service line selections, zone management,
 * and sync status tracking across all tabs
 */

import React, { createContext, useContext, useState, useEffect, ReactNode, Dispatch, SetStateAction } from 'react';
import { Zone } from '../services/zoneManager';
import { SPARC_RFP_DEFAULT_KEYWORDS } from '../services/rssFeedRegistry';
import type { GeographicZoneId } from '../utils/countryAccess';
import { GeographicZoneProvider, useGeographicZone } from './GeographicZoneContext';

export interface GeographicFilter {
  countries: string[];
  provinces: string[];
  cities: string[];
}

export interface ServiceLineFilter {
  selectedLines: string[];
  allLines: string[];
}

export interface SyncStatusInfo {
  lastSync: Date | null;
  syncInProgress: boolean;
  syncedSources: string[];
  failedSources: string[];
}

export interface ExternalDataState {
  // Geographic filtering
  geographicFilter: GeographicFilter;
  setGeographicFilter: (filter: Partial<GeographicFilter>) => void;
  
  // Service line filtering
  serviceLineFilter: ServiceLineFilter;
  setServiceLineFilter: (filter: Partial<ServiceLineFilter>) => void;
  
  // Zone selection (USA, Canada, International)
  selectedZone: Zone;
  setSelectedZone: (zone: Zone) => void;

  /** SPARC-009 geographic zone filter shared across Government Data tabs. */
  selectedGeographicZone: GeographicZoneId;
  setSelectedGeographicZone: Dispatch<SetStateAction<GeographicZoneId>>;
  /** True once auth token country_access has resolved the initial geographic zone. */
  isGeographicZoneReady: boolean;
  
  // Sync status tracking
  syncStatus: SyncStatusInfo;
  setSyncStatus: (status: Partial<SyncStatusInfo>) => void;
  
  // Search and filter state
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  
  // Keywords for monitoring
  selectedKeywords: string[];
  setSelectedKeywords: (keywords: string[]) => void;
  
  // Last refresh timestamp
  lastRefresh: Date | null;
  refreshData: () => void;
}

const ExternalDataContext = createContext<ExternalDataState | undefined>(undefined);

const STORAGE_KEY_PREFIX = 'external-data-dashboard';

interface ExternalDataProviderProps {
  children: ReactNode;
}

function ExternalDataProviderInner({ children }: ExternalDataProviderProps) {
  const {
    selectedGeographicZone,
    setSelectedGeographicZone,
    isGeographicZoneReady,
  } = useGeographicZone();

  // Load initial state from localStorage
  const loadFromStorage = <T,>(key: string, defaultValue: T): T => {
    try {
      const stored = localStorage.getItem(`${STORAGE_KEY_PREFIX}-${key}`);
      return stored ? JSON.parse(stored) : defaultValue;
    } catch {
      return defaultValue;
    }
  };

  // Geographic filter state
  const [geographicFilter, setGeographicFilterState] = useState<GeographicFilter>(
    loadFromStorage('geographic-filter', {
      countries: [],
      provinces: [],
      cities: [],
    })
  );

  // Service line filter state
  const [serviceLineFilter, setServiceLineFilterState] = useState<ServiceLineFilter>(
    loadFromStorage('service-line-filter', {
      selectedLines: [],
      allLines: [],
    })
  );

  // Zone selection state
  const [selectedZone, setSelectedZoneState] = useState<Zone>(
    loadFromStorage('selected-zone', 'usa' as Zone)
  );

  // Sync status state
  const [syncStatus, setSyncStatusState] = useState<SyncStatusInfo>({
    lastSync: null,
    syncInProgress: false,
    syncedSources: [],
    failedSources: [],
  });

  // Search query state
  const [searchQuery, setSearchQuery] = useState<string>(
    loadFromStorage('search-query', '')
  );

  // Keywords state
  const [selectedKeywords, setSelectedKeywordsState] = useState<string[]>(
    loadFromStorage('keywords', [...SPARC_RFP_DEFAULT_KEYWORDS])
  );

  // Last refresh timestamp
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  // Persist geographic filter to localStorage
  useEffect(() => {
    localStorage.setItem(
      `${STORAGE_KEY_PREFIX}-geographic-filter`,
      JSON.stringify(geographicFilter)
    );
  }, [geographicFilter]);

  // Persist service line filter to localStorage
  useEffect(() => {
    localStorage.setItem(
      `${STORAGE_KEY_PREFIX}-service-line-filter`,
      JSON.stringify(serviceLineFilter)
    );
  }, [serviceLineFilter]);

  // Persist zone selection to localStorage
  useEffect(() => {
    localStorage.setItem(
      `${STORAGE_KEY_PREFIX}-selected-zone`,
      JSON.stringify(selectedZone)
    );
  }, [selectedZone]);

  // Persist search query to localStorage
  useEffect(() => {
    localStorage.setItem(
      `${STORAGE_KEY_PREFIX}-search-query`,
      JSON.stringify(searchQuery)
    );
  }, [searchQuery]);

  // Persist keywords to localStorage
  useEffect(() => {
    localStorage.setItem(
      `${STORAGE_KEY_PREFIX}-keywords`,
      JSON.stringify(selectedKeywords)
    );
  }, [selectedKeywords]);

  // Update functions with merge behavior
  const setGeographicFilter = (filter: Partial<GeographicFilter>) => {
    setGeographicFilterState(prev => ({ ...prev, ...filter }));
  };

  const setServiceLineFilter = (filter: Partial<ServiceLineFilter>) => {
    setServiceLineFilterState(prev => ({ ...prev, ...filter }));
  };

  const setSelectedZone = (zone: Zone) => {
    setSelectedZoneState(zone);
  };

  const setSyncStatus = (status: Partial<SyncStatusInfo>) => {
    setSyncStatusState(prev => ({ ...prev, ...status }));
  };

  const setSelectedKeywords = (keywords: string[]) => {
    setSelectedKeywordsState(keywords);
  };

  const refreshData = () => {
    setLastRefresh(new Date());
  };

  const value: ExternalDataState = {
    geographicFilter,
    setGeographicFilter,
    serviceLineFilter,
    setServiceLineFilter,
    selectedZone,
    setSelectedZone,
    selectedGeographicZone,
    setSelectedGeographicZone,
    isGeographicZoneReady,
    syncStatus,
    setSyncStatus,
    searchQuery,
    setSearchQuery,
    selectedKeywords,
    setSelectedKeywords,
    lastRefresh,
    refreshData,
  };

  return (
    <ExternalDataContext.Provider value={value}>
      {children}
    </ExternalDataContext.Provider>
  );
}

export function ExternalDataProvider({ children }: ExternalDataProviderProps) {
  return (
    <GeographicZoneProvider>
      <ExternalDataProviderInner>{children}</ExternalDataProviderInner>
    </GeographicZoneProvider>
  );
}

export function useExternalData(): ExternalDataState {
  const context = useContext(ExternalDataContext);
  if (!context) {
    throw new Error('useExternalData must be used within ExternalDataProvider');
  }
  return context;
}

